import { FillLayer } from 'mapbox-gl'
import { Feature, Geometry, GeoJsonProperties } from 'geojson'
import { MapRef } from 'react-map-gl'
import { ESBoundingBox, FeatureData } from './interfaces'

export const getMapLayers = (mapRef: React.RefObject<MapRef>) => {
  const map = mapRef.current?.getMap()
  const layerRegioni = map?.getLayer('limits-it-regions')
  const layerProvince = map?.getLayer('limits-it-provinces')
  const layerComuni = map?.getLayer('limits-it-municipalities')
  console.log('LAYER DATA', { layerRegioni, layerProvince, layerComuni })
  return [layerRegioni, layerProvince, layerComuni] as FillLayer[]
}

export const getLayerFeatures = (
  mapRef: React.RefObject<MapRef>,
  layers: FillLayer[]
) => {
  const map = mapRef.current?.getMap()

  const features = layers.map((l) => {
    return map?.querySourceFeatures(l.source as string, {
      sourceLayer: (l as any).sourceLayer,
    })
  })
  console.log('FEATURES DATA', features)

  return features
}

export const setupLayerFillColorInterpolation = (
  mapRef: React.RefObject<MapRef>,
  layers: FillLayer[],
  bounds: { [index: string]: [number, number] }
) => {
  const map = mapRef.current?.getMap()

  layers.forEach((l) => {
    map?.setPaintProperty(l.id, 'fill-color', [
      'interpolate',
      ['exponential', 1],
      ['feature-state', 'geo_value'],
      bounds[l.id][0],
      'hsla(196, 59%, 57%, 0.44)',
      bounds[l.id][1],
      'hsla(0, 100%, 48%, 0.44)',
    ])
  })
}

export const setupCircleLayerPaintStyle = (
  mapRef: React.RefObject<MapRef>,
  min_value: number,
  max_value: number
) => {
  const map = mapRef.current?.getMap()

  map?.setPaintProperty('circle-layer', 'circle-radius', [
    'interpolate',
    ['linear'],
    ['zoom'],
    13,
    ['interpolate', ['linear'], ['get', 'value'], min_value, 6, max_value, 10],
    22,
    ['interpolate', ['linear'], ['get', 'value'], min_value, 5, max_value, 50],
  ])

  map?.setPaintProperty('circle-layer', 'circle-color', [
    'interpolate',
    ['linear'],
    ['get', 'value'],
    min_value,
    'rgba(33,102,172,0)',
    max_value,
    'rgb(178,24,43)',
  ])
}

export const setFeaturesState = (
  mapRef: React.RefObject<MapRef>,
  features: mapboxgl.MapboxGeoJSONFeature[],
  data: FeatureData[],
  layer: FillLayer,
  name_label: string
) => {
  const map = mapRef.current?.getMap()

  features?.forEach((f) => {
    const featureName = f.properties?.[name_label]
    const featureValue = data.find((d) => d.name === featureName)

    map?.setFeatureState(
      {
        id: f.id,
        layer: layer,
        source: layer.source as string,
        sourceLayer: (layer as any).sourceLayer,
      },
      { geo_value: featureValue?.value }
    )
  })
}

export const generateData = (
  features: mapboxgl.MapboxGeoJSONFeature[],
  attrName: string
) => {
  return features.map((f) => {
    return {
      name: f.properties?.[attrName],
      value: Math.random() * 100,
    }
  })
}

export const generateRandomFeatures = (
  boundingBox: ESBoundingBox | undefined,
  n: number
) => {
  if (!boundingBox) return []

  const minCoords = {
    lat: Math.min(boundingBox?.bottom_right.lat, boundingBox?.top_left.lat),
    lon: Math.min(boundingBox?.bottom_right.lon, boundingBox?.top_left.lon),
  }

  const maxCoords = {
    lat: Math.max(boundingBox?.bottom_right.lat, boundingBox?.top_left.lat),
    lon: Math.max(boundingBox?.bottom_right.lon, boundingBox?.top_left.lon),
  }

  const features = []
  for (let i = 0; i < n; i++) {
    const coords = [
      minCoords.lon + Math.random() * (maxCoords.lon - minCoords.lon),
      minCoords.lat + Math.random() * (maxCoords.lat - minCoords.lat),
    ]
    const value = Math.random() * 10
    const f: Feature<Geometry, GeoJsonProperties> = {
      id: i,
      type: 'Feature',
      geometry: { type: 'Point', coordinates: coords },
      properties: { name: 'Nome startup ' + i, value: value },
    }
    features.push(f)
  }
  return features
}

export const symmetricDifference = (setA: Set<string>, setB: Set<string>) => {
  const _difference = new Set(setA)

  setB.forEach((elem) => {
    if (_difference.has(elem)) {
      _difference.delete(elem)
    } else {
      _difference.add(elem)
    }
  })

  return _difference
}

export const generateGeoHeatmap = (
  field?: string,
  anno?: number,
  metric?: string,
  agg_key?: string
) => {
  return {
    index: 'infocamere',
    field: field || '',
    metric: metric || 'sum',
    agg_key: agg_key || 'regione',
    logarithm: true,
    query_data: {
      match: [
        {
          field: 'anno',
          value: anno || 2021,
          operator: 'and',
        },
        {
          field: 'mese',
          value: 3,
          operator: 'and',
        },
        {
          field: 'pro_loc',
          value: 0,
          operator: 'and',
        },
      ],
      range: [],
      term: [],
    },
  }
}

export const generateGeoSearch = (
  bbox: ESBoundingBox,
  field?: string,
  anno?: number
) => {
  return {
    index: 'infocamere',
    field: field || '',
    top_left: bbox.top_left,
    bottom_right: bbox.bottom_right,
    query_data: {
      match: [
        {
          field: 'anno',
          value: anno || 2021,
          operator: 'and',
        },
        {
          field: 'mese',
          value: 3,
          operator: 'and',
        },
        {
          field: 'pro_loc',
          value: 0,
          operator: 'and',
        },
      ],
      range: [],
      term: [],
    },
  }
}
